import { BreakpointLayouts, GridLayout, WidgetDefaultSizes } from "../../../models/Dashboard";

export const BREAKPOINT_COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 } as const;

const BREAKPOINT_PLACEMENT = {
    lg: { w: 4, h: 10, minW: 4, minH: 8 },
    md: { w: 5, h: 10, minW: 4, minH: 8 },
    sm: { w: 6, h: 9, minW: 4, minH: 8 },
    xs: { w: 4, h: 9, minW: 4, minH: 8 },
    xxs: { w: 2, h: 9, minW: 2, minH: 8 },
} as const;

type BreakpointKey = keyof typeof BREAKPOINT_COLS;

/**
 * First-fit slot finder: scans rows top→bottom, columns left→right and
 * returns the first position where a `width`×`height` rectangle doesn't
 * collide with any existing item. Falls back to bottom-left if no gap fits.
 */
export const findFirstFreeSlot = (
    items: GridLayout[],
    cols: number,
    width: number,
    height: number
): { x: number; y: number } => {
    const w = Math.min(width, cols);

    const collides = (x: number, y: number) =>
        items.some(
            (it) =>
                x < it.x + it.w &&
                x + w > it.x &&
                y < it.y + it.h &&
                y + height > it.y
        );

    const maxY = items.reduce((max, it) => Math.max(max, it.y + it.h), 0);

    for (let y = 0; y <= maxY; y++) {
        for (let x = 0; x + w <= cols; x++) {
            if (!collides(x, y)) return { x, y };
        }
    }

    return { x: 0, y: maxY };
};

/**
 * Returns a new BreakpointLayouts with `widgetId` placed (first-fit) into
 * every breakpoint. Existing items are untouched.
 */
export const placeWidgetInLayouts = (
    layouts: BreakpointLayouts,
    widgetId: string,
    defaultSizes?: WidgetDefaultSizes
): BreakpointLayouts => {
    const placeOne = (items: GridLayout[] | undefined, key: BreakpointKey): GridLayout[] => {
        const existing = items ?? [];
        if (existing.some((it) => it.i === widgetId)) return existing;

        const cols = BREAKPOINT_COLS[key];
        const fallback = BREAKPOINT_PLACEMENT[key];
        const size = defaultSizes?.[key] ?? fallback;

        const w = Math.min(size.w, cols);
        const h = size.h;
        const minW = Math.min(size.minW ?? fallback.minW, w);
        const minH = size.minH ?? fallback.minH;

        const { x, y } = findFirstFreeSlot(existing, cols, w, h);

        return [...existing, { i: widgetId, x, y, w, h, minW, minH }];
    };

    return {
        lg: placeOne(layouts.lg, "lg"),
        md: placeOne(layouts.md, "md"),
        sm: placeOne(layouts.sm, "sm"),
        xs: placeOne(layouts.xs, "xs"),
        xxs: placeOne(layouts.xxs, "xxs"),
    };
};